import React, { useEffect, useState } from "react";
import styled from "styled-components/macro";
import { compose } from "ramda";
import Grid from "@material-ui/core/Grid";
import { RouteComponentProps } from "react-router-dom";
import axios from "axios";
import { Typography, showToast, BackTo } from "@deeptrue-frontend/ui";

import { parseErrorsToFormik, errorsValidation } from "components/Forms/utils";
import { Dashboard } from "components/Layout";
import { OrganizationType } from "store/modules/auth/auth";
import api from "api/api";
import BillingInfo, {
  SubmitHandler,
} from "components/Forms/BillingInfo/BillingInfo";
import { useFormatMessage } from "components/IntlProvider";
import StoreProvider from "components/StoreProvider/StoreProvider";

const { connect, selectors } = StoreProvider;

const StyledRootContainer = styled.section`
  flex-grow: 1;
  margin-left: 24px;
  margin-right: 24px;
  position: relative;
`;
const StyledCardBlock = styled(Grid)`
  display: flex;
  width: 100%;
  margin-bottom: 2rem;
  flex-direction: column;
`;

interface CreditCardInfoProps extends RouteComponentProps<any> {
  organization: OrganizationType;
}

interface LimitData {
  limit: number;
  available_limit: number;
  reserved_tokens: number;
  available_tokens: number;
}

interface OrganizationAddressInfo {
  id: number;
  address: string;
  postal_code: string;
  city: string;
}

const Invoice = ({ location, history, organization }: CreditCardInfoProps) => {
  const [limitsRes, setLimitsRes] = useState<LimitData>({
    limit: 0,
    available_limit: 0,
    reserved_tokens: 0,
    available_tokens: 0,
  });
  const [addressInfoRes, setAddressInfoRes] = useState<
    OrganizationAddressInfo[]
  >([]);
  const [choosenValues, setChoosenValues] = useState({
    id: -1,
    quantity: "100",
    type: "",
    address: "",
    postal_code: "",
    country: "",
    contact_phone: "",
    additional_address: "",
    city: "",
    contact_email: "",
  });

  const t = useFormatMessage();

  useEffect(() => {
    axios
      .all([
        api.resources.getOrganizationInfo(organization.codename),
        api.resources.getOrganizationPaymentLimits(organization.codename),
      ])
      .then(
        axios.spread((info, limits) => {
          setAddressInfoRes(info.data.results.slice(0, 4));
          setLimitsRes(limits.data);
        })
      );
  }, [organization.codename]);

  const chooseAddress = (values: any) => {
    setChoosenValues({
      id: values.id,
      quantity: "100",
      type: "",
      address: values.address,
      postal_code: values.postal_code,
      country: values.country,
      contact_phone: values.contact_phone || "",
      additional_address: values.additional_address,
      city: values.city,
      contact_email: values.contact_email,
    });
  };

  const removeAddress = (id: number) =>
    api.resources
      .deletePaymentAddress({
        organization_codename: organization.codename,
        id,
      })
      .then(() => {
        showToast({
          type: "success",
          title: t(
            "success.toast.deleteAddress",
            "The address has been deleted"
          ),
        });
        api.resources
          .getOrganizationInfo(organization.codename)
          .then(({ data }) => {
            setAddressInfoRes(data.results.slice(0, 4));
          });
      })
      .catch(() => {
        showToast({
          type: "error",
          title: t(
            "errors.toast.deleteAddress",
            "We cannot delete the address"
          ),
        });
      });

  const onSubmit: SubmitHandler = async (values, formHelpers) => {
    if (choosenValues.id !== -1) {
      return api.resources
        .createPayment({
          organization_codename: organization.codename,
          data: {
            address_info: choosenValues.id,
            quantity: values.quantity,
            type: 2,
          },
        })
        .then(() => {
          history.push("/payments?payment=success&type=invoice");
        })
        .catch(({ response }) => {
          showToast({
            type: "error",
            title: t("errors.toast.payment", "Payment failed"),
          });
          if (errorsValidation.is400Group(response.status)) {
            formHelpers.setErrors(parseErrorsToFormik(response.data));
          }
        })
        .finally(() => {
          formHelpers.setSubmitting(false);
        });
    }
    return api.resources
      .createPaymentAddress({
        organization_codename: organization.codename,
        data: { ...values, organization: organization.id },
      })
      .then((res: any) => {
        api.resources
          .createPayment({
            organization_codename: organization.codename,
            data: {
              address_info: res.data.id,
              quantity: values.quantity,
              type: 2,
            },
          })
          .then(() => {
            history.push("/payments?payment=success&type=invoice");
          })
          .catch(() => {
            showToast({
              type: "error",
              title: t("errors.toast.payment", "Payment failed"),
            });
          });
      })
      .finally(() => {
        formHelpers.setSubmitting(false);
      });
  };

  return (
    <Dashboard title="Invoice" location={location}>
      <Grid item xs={12}>
        <StyledRootContainer>
          <BackTo
            onClick={() => {
              history.goBack();
            }}
          >
            {t("pages.payment.goback", "Back to payments")}
          </BackTo>

          <StyledCardBlock container>
            <Typography variant="h6" {...{ marginTop: "1.5rem" }}>
              {t("pages.payment.title", "Billing data")}
            </Typography>
            <Typography variant="copy1" {...{ marginTop: "0.25rem" }}>
              {t("pages.payment.subtitle", "Add Billing data")}
            </Typography>
            <BillingInfo
              currentBalance={limitsRes.available_tokens}
              initialValues={choosenValues}
              onSubmit={onSubmit}
              addressInfoRes={addressInfoRes}
              removeAddress={removeAddress}
              onAddressChosen={chooseAddress}
            />
          </StyledCardBlock>
        </StyledRootContainer>
      </Grid>
    </Dashboard>
  );
};

export default compose(
  connect((state) => ({
    organization: selectors.authSelectors.userOrganization(state),
  }))
)(Invoice);
